import { useState, useRef, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Scan,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Camera,
  Volume2,
  VolumeX,
  Settings,
  ChevronDown,
  QrCode,
  Keyboard,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn } from '@utils';
import api from '@services/api';

interface ScanResult {
  status: 'valid' | 'invalid' | 'used' | 'expired' | 'error';
  message: string;
  ticket?: {
    id: number;
    ticketNumber: string;
    attendeeName: string;
    eventName: string;
    ticketType: string;
    checkedInAt?: string;
  };
}

export default function ScannerPage() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [cameraId, setCameraId] = useState<string>('');
  const [cameras, setCameras] = useState<{ id: string; label: string }[]>([]);
  const [manualCode, setManualCode] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = 'qr-scanner';

  // Initialize scanner
  useEffect(() => {
    const initScanner = async () => {
      try {
        const devices = await Html5Qrcode.getCameras();
        if (devices && devices.length > 0) {
          setCameras(devices);
          setCameraId(devices[0].id);
        }
      } catch (error) {
        console.error('Failed to get cameras:', error);
        toast.error('Failed to access camera');
      }
    };

    initScanner();

    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    if (!cameraId) {
      toast.error('No camera available');
      return;
    }

    try {
      scannerRef.current = new Html5Qrcode(scannerContainerId);

      await scannerRef.current.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        onScanSuccess,
        onScanError
      );

      setScanning(true);
      setResult(null);
    } catch (error) {
      console.error('Failed to start scanner:', error);
      toast.error('Failed to start scanner');
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current && scanning) {
      try {
        await scannerRef.current.stop();
      } catch (error) {
        console.error('Failed to stop scanner:', error);
      }
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText: string) => {
    // Play beep sound
    if (soundEnabled) {
      playBeep();
    }

    // Stop scanning temporarily
    await stopScanning();

    // Validate ticket
    await validateTicket(decodedText);
  };

  const onScanError = (error: string) => {
    // Ignore scan errors (they happen frequently during scanning)
    console.log('Scan error:', error);
  };

  const validateTicket = async (code: string) => {
    try {
      // Backend uses /checkin/scan with qr_signature payload
      const response = await api.post<{
        valid: boolean;
        message: string;
        data?: {
          ticket?: {
            id: number;
            ticket_number: string;
            attendee_name: string;
            ticketType?: { name: string };
            event?: { name: string };
            checked_in_at?: string;
          };
          attendee_name?: string;
          ticket_type?: string;
        };
      }>('/checkin/scan', { qr_signature: code, event_id: 0 });

      const valid = response.valid;
      const message = response.message;
      const ticketData = response.data?.ticket;
      const alreadyUsed = message?.toLowerCase().includes('already');

      setResult({
        status: valid ? 'valid' : alreadyUsed ? 'used' : 'invalid',
        message,
        ticket: ticketData ? {
          id: ticketData.id,
          ticketNumber: ticketData.ticket_number,
          attendeeName: ticketData.attendee_name || response.data?.attendee_name || 'Unknown',
          eventName: ticketData.event?.name || 'Unknown Event',
          ticketType: ticketData.ticketType?.name || response.data?.ticket_type || 'General',
          checkedInAt: ticketData.checked_in_at,
        } : undefined,
      });

      if (valid) {
        toast.success('Ticket checked in successfully!');
      } else if (alreadyUsed) {
        toast.error('Ticket already used');
      } else {
        toast.error('Invalid ticket');
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.message || error.message || 'Failed to validate ticket';
      const alreadyUsed = errMsg.toLowerCase().includes('already');
      setResult({
        status: alreadyUsed ? 'used' : 'error',
        message: errMsg,
      });
      toast.error(errMsg);
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCode.trim()) return;

    await validateTicket(manualCode.trim());
    setManualCode('');
  };

  const playBeep = () => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1);
  };

  const resetScanner = () => {
    setResult(null);
    startScanning();
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Ticket Scanner</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Scan QR codes to validate and check in tickets
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {cameras.length > 1 && (
            <select
              value={cameraId}
              onChange={(e) => {
                setCameraId(e.target.value);
                if (scanning) {
                  stopScanning().then(() => startScanning());
                }
              }}
              className="input py-2 text-sm"
              disabled={scanning}
              aria-label="Select Camera"
            >
              {cameras.map((camera) => (
                <option key={camera.id} value={camera.id}>
                  {camera.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={cn(
              'p-2 rounded-lg transition-colors',
              soundEnabled
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            )}
            title={soundEnabled ? 'Sound on' : 'Sound off'}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setShowManualInput(!showManualInput)}
            className={cn(
              'p-2 rounded-lg transition-colors',
              showManualInput
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            )}
            title="Manual input"
          >
            <Keyboard className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Scanner */}
      <div className="card overflow-hidden">
        {/* Scanner View */}
        <div className="relative aspect-square bg-black">
          <div id={scannerContainerId} className="w-full h-full" />

          {/* Overlay */}
          {!scanning && !result && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900/90">
              <div className="text-center">
                <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-white font-medium">Camera is off</p>
                <button onClick={startScanning} className="btn-primary mt-4">
                  <Scan className="w-4 h-4" />
                  Start Scanning
                </button>
              </div>
            </div>
          )}

          {/* Scanning Frame */}
          {scanning && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-64 h-64">
                  {/* Corner markers */}
                  <div className="absolute top-0 left-0 w-8 h-8 border-l-4 border-t-4 border-primary-500"></div>
                  <div className="absolute top-0 right-0 w-8 h-8 border-r-4 border-t-4 border-primary-500"></div>
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-l-4 border-b-4 border-primary-500"></div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-r-4 border-b-4 border-primary-500"></div>

                  {/* Scan line */}
                  <motion.div
                    className="absolute left-0 right-0 h-0.5 bg-primary-500 shadow-glow"
                    animate={{
                      top: ['0%', '100%', '0%'],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  />
                </div>
              </div>

              {/* Stop button */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                <button onClick={stopScanning} className="btn-danger">
                  <XCircle className="w-4 h-4" />
                  Stop Scanning
                </button>
              </div>
            </div>
          )}

          {/* Result Overlay */}
          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="absolute inset-0 flex items-center justify-center bg-gray-900/95 p-6"
              >
                <div className="text-center max-w-sm">
                  {/* Status Icon */}
                  <div
                    className={cn(
                      'w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4',
                      result.status === 'valid' && 'bg-success-100 text-success-600',
                      result.status === 'used' && 'bg-warning-100 text-warning-600',
                      result.status === 'invalid' && 'bg-danger-100 text-danger-600',
                      result.status === 'error' && 'bg-gray-100 text-gray-600'
                    )}
                  >
                    {result.status === 'valid' && <CheckCircle className="w-12 h-12" />}
                    {result.status === 'used' && <AlertCircle className="w-12 h-12" />}
                    {result.status === 'invalid' && <XCircle className="w-12 h-12" />}
                    {result.status === 'error' && <AlertCircle className="w-12 h-12" />}
                  </div>

                  {/* Status Text */}
                  <h3
                    className={cn(
                      'text-2xl font-bold mb-2',
                      result.status === 'valid' && 'text-success-600',
                      result.status === 'used' && 'text-warning-600',
                      result.status === 'invalid' && 'text-danger-600',
                      result.status === 'error' && 'text-gray-600'
                    )}
                  >
                    {result.status === 'valid' && 'Valid Ticket!'}
                    {result.status === 'used' && 'Already Used'}
                    {result.status === 'invalid' && 'Invalid Ticket'}
                    {result.status === 'error' && 'Error'}
                  </h3>
                  <p className="text-gray-300 mb-6">{result.message}</p>

                  {/* Ticket Details */}
                  {result.ticket && (
                    <div className="bg-white/10 rounded-lg p-4 mb-6 text-left">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Ticket #</span>
                          <span className="text-white font-medium">{result.ticket.ticketNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Attendee</span>
                          <span className="text-white font-medium">{result.ticket.attendeeName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Event</span>
                          <span className="text-white font-medium">{result.ticket.eventName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Type</span>
                          <span className="text-white font-medium">{result.ticket.ticketType}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <button onClick={resetScanner} className="btn-primary w-full">
                    <RefreshCw className="w-4 h-4" />
                    Scan Next Ticket
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Manual Input */}
      <AnimatePresence>
        {showManualInput && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card p-4"
          >
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Manual Ticket Code
            </h3>
            <form onSubmit={handleManualSubmit} className="flex gap-3">
              <input
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Enter ticket code..."
                className="input flex-1"
              />
              <button type="submit" className="btn-primary">
                <QrCode className="w-4 h-4" />
                Validate
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Instructions */}
      <div className="card p-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          How to scan
        </h3>
        <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
          <li>1. Position the QR code within the frame</li>
          <li>2. Hold steady until the code is recognized</li>
          <li>3. Wait for the validation result</li>
          <li>4. Use manual input if scanning fails</li>
        </ul>
      </div>
    </div>
  );
}
